import { useEffect, createContext, useReducer } from "react";
import jwtDecode from "jwt-decode";
import axios from "utils/axios";
import LoadingScreen from "components/LoadingScreen";

//initial State value
const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

//crate context
const AuthContext = createContext({ ...initialState });

//check token validation
const isValidToken = (accessToken) => {
  if (!accessToken) return false;

  const decodedToken = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decodedToken.exp > currentTime;
};

//save token to local store
const setToken = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("accessToken", accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem("accessToken");
    delete axios.defaults.headers.common.Authorization;
  }
};

//reducer
const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      return {
        isInitialized: true,
        user: action.payload.user,
        isAuthenticated: action.payload.isAuthenticated,
      };
    }
    case "LOGIN": {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        user: null,
        isAuthenticated: false,
      };
    }
    case "PROFILE_UPDATE": {
      return {
        ...state,
        user: action.payload.user,
      };
    }
    default: {
      return state;
    }
  }
};

//auth provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  //login
  const login = async (username, password) => {
    const response = await axios.post("/api/auth/login", {
      username,
      password,
    });

    const { token, user } = response.data;

    setToken(token);
    dispatch({ type: "LOGIN", payload: { user } });
  };

  //change password
  const changePassword = async (oldPassword, newPassword) => {
    const { data } = await axios.post("/api/auth/change-password", {
      oldPassword,
      newPassword,
    });

    setToken(data.token);
  };

  //logout
  const logout = () => {
    setToken(null);
    dispatch({ type: "LOGOUT" });
  };

  //profile update
  const profileUpdate = async (formData) => {
    const { data } = await axios.patch("/api/auth/profile", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    dispatch({ type: "PROFILE_UPDATE", payload: { user: data.user } });

    return data;
  };

  //get user info
  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");

        if (accessToken && isValidToken(accessToken)) {
          setToken(accessToken);

          const response = await axios.get("/api/auth/profile");
          const { user } = response.data;

          dispatch({
            type: "INIT",
            payload: {
              user,
              isAuthenticated: true,
            },
          });
        } else {
          setToken(null);
          dispatch({
            type: "INIT",
            payload: {
              user: null,
              isAuthenticated: false,
            },
          });
        }
      } catch (err) {
        console.error(err);
        setToken(null);
        dispatch({
          type: "INIT",
          payload: {
            user: null,
            isAuthenticated: false,
          },
        });
      }
    })();
  }, []);

  //show loading if not init
  if (!state.isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        changePassword,
        profileUpdate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
